import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';

export default function SignupPage() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '', birthday: '' });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await signup(form);
      toast.success('Account created!');
      nav('/menu');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto flex max-w-md flex-col gap-4 px-4 py-10 sm:px-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-2xl">Create your account</CardTitle>
            <CardDescription>Order faster and track your meals live.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input data-testid="signup-name-input" id="name" required value={form.full_name} onChange={update('full_name')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input data-testid="signup-email-input" id="email" type="email" required value={form.email} onChange={update('email')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input data-testid="signup-phone-input" id="phone" value={form.phone} onChange={update('phone')} placeholder="+1 555 123 4567" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="birthday">Birthday (optional)</Label>
                <Input
                  data-testid="signup-birthday-input"
                  id="birthday"
                  type="date"
                  value={form.birthday}
                  onChange={update('birthday')}
                  max={(() => {
                    // Must be at least 15 years old. Compute the latest
                    // allowable DOB (today minus 15 years) so the browser
                    // date picker enforces it client-side too.
                    const d = new Date();
                    d.setFullYear(d.getFullYear() - 15);
                    return d.toISOString().slice(0, 10);
                  })()}
                />
                <p className="text-xs text-muted-foreground">You must be at least 15 years old. <strong>This is locked once your account is created</strong> \u2014 we use it for your birthday reward.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input data-testid="signup-password-input" id="password" type="password" minLength={8} required value={form.password} onChange={update('password')} />
                <p className="text-xs text-muted-foreground">Minimum 8 characters. Stored using bcrypt.</p>
              </div>
              <Button data-testid="signup-submit-button" type="submit" className="w-full h-11" disabled={loading}>{loading ? 'Creating account…' : 'Create account'}</Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              Already a member? <Link to="/login" data-testid="signup-login-link" className="font-medium text-primary hover:underline">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
